import React, { useState, useEffect } from 'react';
import { TextField, PrimaryButton, DefaultButton, Text, MessageBar, MessageBarType, Spinner, SpinnerSize, Stack, Dialog, DialogType, DialogFooter } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { useModalContext } from './ModalProvider';
import { userAPI } from '../UserAPI';

interface StripeAccountFormProps {
    customerId: string;
    onSuccess?: (accountData: any) => void;
    onCancel?: () => void;
}

interface AccountData {
    business_type: 'individual' | 'company';
    company?: {
        name: string;
        tax_id?: string;
    };
    individual?: {
        first_name: string;
        last_name: string;
        email: string;
        phone?: string;
        dob?: {
            day: number;
            month: number;
            year: number;
        };
    };
    business_profile?: {
        url?: string;
        mcc?: string;
    };
    capabilities?: {
        card_payments?: { requested: boolean };
        transfers?: { requested: boolean };
    };
}

interface StripeAccount {
    id: string;
    business_type: 'individual' | 'company';
    company?: {
        name: string;
        tax_id?: string;
    };
    individual?: {
        first_name: string;
        last_name: string;
        email: string;
        phone?: string;
    };
    business_profile?: {
        url?: string;
        mcc?: string;
    };
    charges_enabled: boolean;
    payouts_enabled: boolean;
    requirements: {
        currently_due: string[];
        eventually_due: string[];
        past_due: string[];
    };
}

export default function StripeAccountForm({ customerId, onSuccess, onCancel }: StripeAccountFormProps) {
    const { t } = useTranslation();
    const { pushError } = useModalContext() as any;
    const [loading, setLoading] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [isLinkingExisting, setIsLinkingExisting] = useState(false);
    const [existingAccountId, setExistingAccountId] = useState('');
    const [accountData, setAccountData] = useState<AccountData>({
        business_type: 'individual',
        individual: {
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
        },
        company: {
            name: '',
            tax_id: '',
        },
        business_profile: {
            url: '',
            mcc: '',
        },
        capabilities: {
            card_payments: { requested: true },
            transfers: { requested: true },
        },
    });
    const [stripeAccount, setStripeAccount] = useState<StripeAccount | null>(null);

    useEffect(() => {
        loadStripeAccount();
    }, [customerId]);

    const loadStripeAccount = async () => {
        try {
            const account = await userAPI.getStripeAccount(customerId);
            setStripeAccount(account);
        } catch (error) {
            // Account doesn't exist yet, which is fine
            console.log('No existing Stripe account found');
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setLoading(true);

        try {
            let result;
            if (isLinkingExisting) {
                result = await userAPI.linkStripeAccount(customerId, existingAccountId);
            } else {
                result = await userAPI.createStripeAccount(customerId, accountData);
            }
            setStripeAccount(result);
            setShowDialog(false);
            onSuccess?.(result);
        } catch (error) {
            console.error('Error with Stripe account:', error);
            pushError(error instanceof Error ? error : new Error(isLinkingExisting ? 'Failed to link Stripe account' : 'Failed to create Stripe account'));
        } finally {
            setLoading(false);
        }
    };

    const updateAccountData = (field: string, value: any) => {
        setAccountData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const updateIndividual = (field: string, value: any) => {
        setAccountData(prev => ({
            ...prev,
            individual: {
                ...prev.individual!,
                [field]: value
            }
        }));
    };

    const updateCompany = (field: string, value: any) => {
        setAccountData(prev => ({
            ...prev,
            company: {
                ...prev.company!,
                [field]: value
            }
        }));
    };

    const updateBusinessProfile = (field: string, value: any) => {
        setAccountData(prev => ({
            ...prev,
            business_profile: {
                ...prev.business_profile,
                [field]: value
            }
        }));
    };

    const translateRequirement = (requirement: string): string => {
        return t(`stripe.requirements.${requirement}`, requirement);
    };

    const renderAccountData = () => {
        if (!stripeAccount) {
            return (
                <Stack tokens={{ childrenGap: 16 }}>
                    <div className='textM'>Dieses Konto wird gebraucht, um euch Zahlungen für die Nutzung eurer Dienste und Wissenbasen zu überweisen.</div>
                    <MessageBar messageBarType={MessageBarType.info}>
                        <Text variant="small">
                            Kein Stripe-Konto konfiguriert. Erstellen Sie ein neues Konto oder verknüpfen Sie ein bestehendes Stripe Konto.
                        </Text>
                    </MessageBar>
                    <Stack horizontal tokens={{ childrenGap: 12 }}>
                        <PrimaryButton
                            text="Stripe-Konto erstellen"
                            onClick={() => {
                                setIsLinkingExisting(false);
                                setShowDialog(true);
                            }}
                        />
                        <DefaultButton
                            text="Bestehendes Stripe-Konto verknüpfen"
                            onClick={() => {
                                setIsLinkingExisting(true);
                                setShowDialog(true);
                            }}
                        />
                    </Stack>
                </Stack>
            );
        }

        return (
            <Stack tokens={{ childrenGap: 16 }}>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                    <Text variant="large" className="fontWeightBold">
                        {stripeAccount.business_type === 'individual' 
                            ? `${stripeAccount.individual?.first_name} ${stripeAccount.individual?.last_name}`
                            : stripeAccount.company?.name
                        }
                    </Text>
                    <DefaultButton
                        text="Bearbeiten"
                        onClick={() => setShowDialog(true)}
                    />
                </Stack>

                <Stack tokens={{ childrenGap: 8 }}>
                    <Text variant="medium" className="fontWeightBold">Kontostatus</Text>
                    <Stack horizontal tokens={{ childrenGap: 16 }}>
                        <Text variant="small">
                            Zahlungen: {stripeAccount.charges_enabled ? 'Aktiviert' : 'Deaktiviert'}
                        </Text>
                        <Text variant="small">
                            Auszahlungen: {stripeAccount.payouts_enabled ? 'Aktiviert' : 'Deaktiviert'}
                        </Text>
                    </Stack>
                </Stack>

                {stripeAccount.requirements.currently_due.length > 0 && (
                    <MessageBar messageBarType={MessageBarType.warning}>
                        <Text variant="small">
                            Erforderliche Dokumente: {stripeAccount.requirements.currently_due.map(translateRequirement).join(', ')}
                        </Text>
                    </MessageBar>
                )}

                <Stack tokens={{ childrenGap: 8 }}>
                    <Text variant="medium" className="fontWeightBold">Kontaktdaten</Text>
                    {stripeAccount.business_type === 'individual' ? (
                        <Stack tokens={{ childrenGap: 4 }}>
                            <Text variant="small">E-Mail: {stripeAccount.individual?.email}</Text>
                            {stripeAccount.individual?.phone && (
                                <Text variant="small">Telefon: {stripeAccount.individual.phone}</Text>
                            )}
                        </Stack>
                    ) : (
                        <Stack tokens={{ childrenGap: 4 }}>
                            {stripeAccount.company?.tax_id && (
                                <Text variant="small">Steuernummer: {stripeAccount.company.tax_id}</Text>
                            )}
                        </Stack>
                    )}
                </Stack>

                {stripeAccount.business_profile?.url && (
                    <Stack tokens={{ childrenGap: 8 }}>
                        <Text variant="medium" className="fontWeightBold">Website</Text>
                        <Text variant="small">{stripeAccount.business_profile.url}</Text>
                    </Stack>
                )}
            </Stack>
        );
    };

    return (
        <>
            {renderAccountData()}

            <Dialog
                hidden={!showDialog}
                onDismiss={() => setShowDialog(false)}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: isLinkingExisting ? 'Bestehendes Stripe Konto verknüpfen' : 'Stripe Konto erstellen',
                }}
                modalProps={{
                    isBlocking: false,
                    styles: { 
                        main: { 
                            maxWidth: '800px !important',
                            width: '800px !important'
                        } 
                    }
                }}
            >
                <form onSubmit={handleSubmit}>
                    <Stack tokens={{ childrenGap: 16 }}>
                        {isLinkingExisting ? (
                            <>
                                <MessageBar messageBarType={MessageBarType.info}>
                                    <Text variant="small">
                                        Geben Sie Ihre Stripe Account ID ein, um ein bestehendes Konto zu verknüpfen.
                                        Sie finden diese in Ihrem Stripe Dashboard unter Einstellungen → Account Details.
                                    </Text>
                                </MessageBar>
                                <TextField
                                    label="Stripe Account ID"
                                    placeholder="acct_..."
                                    value={existingAccountId}
                                    onChange={(_, newValue) => setExistingAccountId(newValue || '')}
                                    required
                                    description="Ihre Stripe Account ID (beginnt mit 'acct_')"
                                />
                            </>
                        ) : (
                            <MessageBar messageBarType={MessageBarType.info}>
                                <Text variant="small">
                                    Um Zahlungen über den Marktplatz zu erhalten, benötigen wir Ihre Kontodaten für Stripe Connect.
                                    Diese werden sicher an Stripe übertragen und für die Auszahlung von Einnahmen verwendet.
                                </Text>
                            </MessageBar>
                        )}

                        <Stack horizontal tokens={{ childrenGap: 16 }}>
                            <DefaultButton
                                text="Privatperson"
                                checked={accountData.business_type === 'individual'}
                                onClick={() => updateAccountData('business_type', 'individual')}
                            />
                            <DefaultButton
                                text="Unternehmen"
                                checked={accountData.business_type === 'company'}
                                onClick={() => updateAccountData('business_type', 'company')}
                            />
                        </Stack>

                        {accountData.business_type === 'individual' ? (
                            <Stack tokens={{ childrenGap: 12 }}>
                                <Text variant="medium" className="fontWeightBold">Persönliche Daten</Text>
                                <Stack horizontal tokens={{ childrenGap: 12 }}>
                                    <TextField
                                        label="Vorname"
                                        value={accountData.individual?.first_name || ''}
                                        onChange={(_, newValue) => updateIndividual('first_name', newValue || '')}
                                        required
                                    />
                                    <TextField
                                        label="Nachname"
                                        value={accountData.individual?.last_name || ''}
                                        onChange={(_, newValue) => updateIndividual('last_name', newValue || '')}
                                        required
                                    />
                                </Stack>
                                <TextField
                                    label="E-Mail"
                                    type="email"
                                    value={accountData.individual?.email || ''}
                                    onChange={(_, newValue) => updateIndividual('email', newValue || '')}
                                    required
                                />
                                <TextField
                                    label="Telefon (optional)"
                                    value={accountData.individual?.phone || ''}
                                    onChange={(_, newValue) => updateIndividual('phone', newValue || '')}
                                />
                            </Stack>
                        ) : (
                            <Stack tokens={{ childrenGap: 12 }}>
                                <Text variant="medium" className="fontWeightBold">Unternehmensdaten</Text>
                                <TextField
                                    label="Firmenname"
                                    value={accountData.company?.name || ''}
                                    onChange={(_, newValue) => updateCompany('name', newValue || '')}
                                    required
                                />
                                <TextField
                                    label="Steuernummer (optional)"
                                    value={accountData.company?.tax_id || ''}
                                    onChange={(_, newValue) => updateCompany('tax_id', newValue || '')}
                                />
                            </Stack>
                        )}

                        <Stack tokens={{ childrenGap: 12 }}>
                            <Text variant="medium" className="fontWeightBold">Geschäftsprofil</Text>
                            <TextField
                                label="Website (optional)"
                                value={accountData.business_profile?.url || ''}
                                onChange={(_, newValue) => updateBusinessProfile('url', newValue || '')}
                            />
                            <TextField
                                label="MCC Code (optional)"
                                value={accountData.business_profile?.mcc || ''}
                                onChange={(_, newValue) => updateBusinessProfile('mcc', newValue || '')}
                                description="Merchant Category Code für Ihr Geschäft"
                            />
                        </Stack>
                    </Stack>

                    <DialogFooter>
                        <PrimaryButton
                            type="submit"
                            disabled={loading}
                            text={loading ? 'Wird verarbeitet...' : (isLinkingExisting ? 'Konto verknüpfen' : 'Konto erstellen')}
                        />
                        <DefaultButton
                            onClick={() => setShowDialog(false)}
                            disabled={loading}
                            text="Abbrechen"
                        />
                    </DialogFooter>
                </form>
            </Dialog>
        </>
    );
} 